import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  Input,
  OnInit,
  Output,
  Renderer2,
  ViewChild,
} from '@angular/core';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { CookieService } from 'ngx-cookie-service';
import { Project, VersionInfo } from 'src/app/core/objects/project';
import { User } from 'src/app/core/objects/user';
import { ProjectService } from 'src/app/core/services/project.service';
import { UserApiService } from 'src/app/core/services/user-api.service';
import {
  Axle,
  LoadingSide,
  LoadingSpaceType,
  WorkSpaceStop,
} from 'src/app/core/types';
import { DISABLED_INITIAL_ANIMATION, WIDTH_ANIMATION } from 'src/app/shared/animations/animations';
import { ProjectApiService } from './../../core/services/project-api.service';
import { UserService } from './../../core/services/user.service';
import { LoadingSpaceAirDimensionsComponent } from './components/loading-space-air-dimensions/loading-space-air-dimensions.component';
import {
  LoadingSpaceDimensionsComponent,
  LoadingSpacePartDimensions,
} from './components/loading-space-dimensions/loading-space-dimensions.component';
import { LoadingSpacePalletDimensionsComponent } from './components/loading-space-pallet-dimensions/loading-space-pallet-dimensions.component';
import { Observable } from 'rxjs';
import { SharedChangesEditPopupComponent } from '../../container/shared-changes-edit-popup/shared-changes-edit-popup.component';
import { ModalService } from '../../modal/modal.service';
import { LoadingSpace } from '../../working-table/visualisation/loading-space';
import {
  getFromLocalStorage,
  isMoreThanDaysAgo,
  setLocalStorage,
} from '../utils/localStorageHelpers';
import {
  convertLoadingSpaceDimensions,
  convertLoadingSpacesDimensions,
} from '../../shared/helpers/unitRecalculationHelpers';

export interface FormEvent extends Event {
  gravityTrailerMaxDeviation?: number;
  gravitySemiTrailerMaxDeviation?: number;
  gravitySemiTrailerLengthTolerance?: number;
  gravityShowCenter?: true;
  gravityTrailerLengthTolerance?: number;
  width?: string;
  width2?: string;
  height?: string;
  height2?: string;
  firstPart: LoadingSpacePartDimensions;
  secondPart: LoadingSpacePartDimensions;
  limit: string;
  length: string;
  set?: boolean;
  length2?: string;
  distance?: string;
  distance2?: string;
  name: string | null;
  width1?: string;
  height1?: string;
  length1?: string;
  loading?: string;
  limit1?: string;
  floorHeight?: string;
  floorHeight2?: string;
  floorThickness?: string;
  floorThickness2?: string;
  floorMaximumCapacity?: string;
  floorMaximumCapacity2?: string;
  airType?: number;
  airLengthA?: number;
  airWidth?: number;
  airBaseB?: number;
  airHeightC?: number;
  airHeightD?: number;
  airLoadCapacity?: number;
  airQuantity?: number;
  palletWeight?: string;
  palletMaxHeight?: string;
  palletMaxCapacity?: string;
  palletQuantity?: string;
  palletStacking?: boolean;
  palletFloorNumber?: number;
  palletFloorWeight?: boolean;
  palletMaxWeight?: number;
  palletVehicleToLoad?: boolean;
  palletVehicle?: number;
  trailerDoorHeight?: string;
  semiTrailerDoorHeight?: string;
  trailerDoorWidth?: string;
  semiTrailerDoorWidth?: string;
  doorHeight?: string;
  doorWidth?: string;
  airLoadingSide?: LoadingSide;
  trailerLoadingSide?: LoadingSide;
  semiTrailerLoadingSide?: LoadingSide;
  palletLoadingSide?: LoadingSide;
  palletHeight?: LoadingSide;
  gravityTrailerWidthTolerance?: number;
  trailerContainerWeight?: number;
  gravitySemiTrailerWidthTolerance?: number;
  semiTrailerContainerWeight?: number;
}

export const emptyLoadingSpace: LoadingSpaceType = {
  name: '',
  length: null,
  width: null,
  height: null,
  limit: null,
  length1: null,
  width1: null,
  height1: null,
  limit1: null,
  set: false,
  type: null,
  id: null,
  unit: null,
  axis: [],
  wheelTrailerWidth: null,
  wheelTrailerHeight: null,
  wheelTrailerLength: null,
  wheelTrailerDistance: null,
  wheelSemiTrailerWidth: null,
  wheelSemiTrailerHeight: null,
  wheelSemiTrailerLength: null,
  wheelSemiTrailerDistance: null,
  coolerTrailerWidth: null,
  coolerTrailerHeight: null,
  coolerTrailerLength: null,
  coolerTrailerFloorHeight: null,
  coolerSemiTrailerWidth: null,
  coolerSemiTrailerHeight: null,
  coolerSemiTrailerLength: null,
  coolerSemiTrailerFloorHeight: null,
  varioTrailerFloorHeight: null,
  varioSemiTrailerFloorHeight: null,
  varioTrailerFloorThickness: null,
  varioSemiTrailerFloorThickness: null,
  varioTrailerFloorCapacity: null,
  varioSemiTrailerFloorCapacity: null,
  trailerLoadingSide: null,
  semiTrailerLoadingSide: null,
  airType: null,
  airLengthA: null,
  airWidth: null,
  airBaseB: null,
  airHeightC: null,
  airHeightD: null,
  palletWeight: null,
  palletMaxHeight: null,
  palletStacking: null,
  palletFloorNumber: null,
  palletFloorWeight: null,
  palletMaxWeight: null,
  trailerDoorHeight: null,
  semiTrailerDoorHeight: null,
  trailerDoorWidth: null,
  semiTrailerDoorWidth: null,
  gravitySemiTrailerLengthTolerance: null,
  gravityShowCenter: null,
  gravityTrailerLengthTolerance: null,
  palletHeight: null,
  gravityTrailerWidthTolerance: null,
  trailerContainerWeight: null,
  gravitySemiTrailerWidthTolerance: null,
  semiTrailerContainerWeight: null,
};

@Component({
    selector: 'gl-add-loading-space',
    templateUrl: './add-loading-space.component.html',
    styleUrls: ['./add-loading-space.component.scss'],
    animations: [WIDTH_ANIMATION, DISABLED_INITIAL_ANIMATION],
    standalone: false
})
export class AddLoadingSpaceComponent implements OnInit, AfterViewInit {
  @HostBinding('class.checkboxActive') public checkboxActive = false;
  @Output() public submitted: EventEmitter<LoadingSpaceType> = new EventEmitter<LoadingSpaceType>();
  @Output() public closeAddLoadingSpace: EventEmitter<undefined> = new EventEmitter<undefined>();
  @ViewChild('dimensions')
  public dimensionsComponent:
    | LoadingSpaceDimensionsComponent
    | LoadingSpaceAirDimensionsComponent
    | LoadingSpacePalletDimensionsComponent;
  @Input() isQuick = false;
  public chosenType = 1;
  public typeForm: TypeForm = this.generateTypesForm();
  public filteredLoadingSpaces: LoadingSpaceType[];
  public loadingSpaces: LoadingSpaceType[];
  public chosenLoadingSpace: LoadingSpaceType;
  public isSortByNameEnabled: boolean;
  public user: User;
  public nameToSend: string;
  private idLoadingSpace: number;
  public loadingSpace: LoadingSpaceType = emptyLoadingSpace;
  public isTabSet = false;
  public isTabAxle = false;
  public isTabGravityCenter = false;
  public isTabWheelArches = false;
  public isTabCooler = false;
  public isTabDoorSize = false;
  public isTabVarioFloor = false;
  public isPalletStacking = false;
  public isPalletVehicleLoading = false;
  public isAirWeightCenter = false;
  public isEdit = false;
  public isEditMultistop = false;
  public savedStops: WorkSpaceStop[];
  public project: Project;
  public isListVisible = true;
  private searchValue = '';
  private isQuickMode = false;
  private openedLoadingSpace = '';
  private isSharedProjectLast = true;

  constructor(
    private renderer: Renderer2,
    private elRef: ElementRef<HTMLElement>,
    private projectApi: ProjectApiService,
    private projectService: ProjectService,
    private userService: UserService,
    private userApi: UserApiService,
    private translate: TranslateService,
    private cookieService: CookieService,
    private modalService: ModalService,
  ) {
    this.isSortByNameEnabled = this.cookieService.get('sortByName').includes('true') ? true : false;
    this.projectService.project.subscribe((project) => (this.project = project));
    this.userService.user.subscribe((user) => {
      this.user = user;
      user.trucks.user.forEach((truck) => (truck.isUser = true));
      const isMm = this.project.options.unit === 'mm';
      if (isMm) {
        const sorted = this.sortByVolume(user.trucks.standard.concat(user.trucks.user));
        this.loadingSpaces = convertLoadingSpacesDimensions(sorted, true);
      } else {
        this.loadingSpaces = this.sortByVolume(user.trucks.standard.concat(user.trucks.user));
      }
    });
  }

  private displaySharedChangesEditPopup(): Observable<any> {
    return this.modalService.open(SharedChangesEditPopupComponent);
  }

  public ngOnInit(): void {
    this.getProjectVersions();
    this.isQuickMode = this.isQuick;
    this.filteredLoadingSpaces = this.filterLoadingSpaceByType(this.typeForm.value.type);
    this.typeForm.valueChanges.subscribe((value: TypeForm['value']) => {
      if (this.chosenType !== value.type) {
        this.closeTabs();
        this.loadingSpace = emptyLoadingSpace;
        this.chosenLoadingSpace = emptyLoadingSpace;
        this.savedStops = undefined;
        if (this.dimensionsComponent) {
          this.dimensionsComponent.resetForm();
        }
        this.isEdit = false;
        this.filteredLoadingSpaces =
          value.type === 3
            ? this.mapPalletHeights(this.filterLoadingSpaceByType(value.type))
            : this.filterLoadingSpaceByType(value.type);
        if (this.searchValue) {
          this.filteredLoadingSpaces = this.filteredLoadingSpaces.filter((space) => {
            return space.name.toLowerCase().includes(this.searchValue.toLowerCase());
          });
        }
        this.chosenType = value.type;
        this.checkboxActive = false;
      }
    });
  }

  public ngAfterViewInit(): void {
    if (this.dimensionsComponent && this.dimensionsComponent.form) {
      this.dimensionsComponent.form.valueChanges.subscribe(() => {
        setTimeout(() => {
          if (this.dimensionsComponent && this.dimensionsComponent.form) {
            this.nameToSend = this.dimensionsComponent.form.value.name;
          }
        });
      });
    }
  }

  private mapPalletHeights(loadingSpaceByType: LoadingSpaceType[]) {
    return loadingSpaceByType.map((loadingSpace) => ({
      ...loadingSpace,
      palletMaxHeight: loadingSpace.height,
    }));
  }

  public listVisibleChanger(): void {
    this.isListVisible = !this.isListVisible;
  }

  public openMultistop(): void {
    this.isEditMultistop = !this.isEditMultistop;
  }

  public submitStops(stops: WorkSpaceStop[]): void {
    this.savedStops = stops;
    this.openMultistop();

    if (this.loadingSpace && !this.isEdit) {
      this.editLoadingSpace(this.loadingSpace);
    } else if (!this.loadingSpace && !this.isEdit) {
      this.createOwnContainer();
    }
    setTimeout(() => {
      if (stops?.length > 0 && this.dimensionsComponent.form.get('firstPart')) {
        this.dimensionsComponent.form.get('firstPart').get('quantity').setValue(1);
        this.dimensionsComponent.form.get('firstPart').get('quantity').disable();
      } else if (stops?.length > 0 && this.dimensionsComponent.form.get('airQuantity')) {
        this.dimensionsComponent.form.get('airQuantity').setValue(1);
        this.dimensionsComponent.form.get('airQuantity').disable();
      } else if (!stops && this.dimensionsComponent.form.get('firstPart')) {
        this.dimensionsComponent.form.get('firstPart').get('quantity').enable();
      } else if (!stops && this.dimensionsComponent.form.get('airQuantity')) {
        this.dimensionsComponent.form.get('airQuantity').enable();
      }
    }, 100);
  }

  public getQuantity(): number {
    let value = 0;
    if (this.dimensionsComponent?.form?.get('firstPart')) {
      value = parseFloat(this.dimensionsComponent.form.get('firstPart').get('quantity').value);
    } else if (this.dimensionsComponent?.form?.get('airQuantity')) {
      value = parseFloat(this.dimensionsComponent.form.get('airQuantity').value);
    }

    return value >= 0 ? value : 0;
  }

  public sortByNameSwitch(): void {
    this.isSortByNameEnabled = !this.isSortByNameEnabled;
    this.cookieService.get('sortByName').includes('true')
      ? this.cookieService.set('sortByName', 'false', undefined, '/', null, false, 'Lax')
      : this.cookieService.set('sortByName', 'true', undefined, '/', null, false, 'Lax');
    if (this.isSortByNameEnabled) {
      this.filteredLoadingSpaces = this.sortByName(this.filteredLoadingSpaces);
    } else {
      this.filteredLoadingSpaces = this.sortByVolume(this.filteredLoadingSpaces);
    }
  }

  public close(): void {
    if (!this.isEditMultistop) {
      this.closeAddLoadingSpace.emit();
    }
  }

  public sortByName(loadingSpaces: LoadingSpaceType[]): LoadingSpaceType[] {
    this.isSortByNameEnabled = true;
    this.cookieService.set('sortByName', 'true', undefined, '/', null, false, 'Lax');
    const filteredLoadingSpaces = loadingSpaces.map((loadingSpace: LoadingSpaceType) => {
      return {
        ...loadingSpace,
        translatedName: this.translateLoadingSpaceName(loadingSpace.name),
      };
    });
    return [...this.userService.sortByTranslatedName(filteredLoadingSpaces)] as LoadingSpaceType[];
  }

  public onFormData(event: FormEvent, kind: string): void {
    const isPallet = kind === 'pallet' && event.width && event.palletHeight && event.length;
    const isAirContainer = kind === 'air' && event.airType !== null && event.airType !== undefined;
    const isValid =
      event.firstPart &&
      event.length !== null &&
      event.firstPart.width !== null &&
      event.firstPart.height !== null;
    let loadingSpace: LoadingSpaceType;
    if (isAirContainer) {
      loadingSpace = {
        ...this.loadingSpace,
        name: event.name,
        airType: event.airType,
        airLengthA: event.airLengthA,
        airWidth: event.airWidth,
        airBaseB: event.airBaseB,
        airHeightC: event.airHeightC,
        airHeightD: event.airHeightD,
        trailerLoadingSide: 'top',
        semiTrailerLoadingSide: 'top',
      };
      this.loadingSpace = loadingSpace;
    }
    if (isPallet) {
      loadingSpace = {
        ...this.loadingSpace,
        name: event.name,
        width: parseFloat(event.width),
        height: parseFloat(event.palletMaxHeight),
        palletHeight: parseFloat(event.palletHeight),
        length: parseFloat(event.length),
        palletWeight: parseFloat(event.palletWeight),
        palletMaxHeight: parseFloat(event.palletMaxHeight),
        trailerLoadingSide: 'top',
        semiTrailerLoadingSide: 'top',
        limit: parseFloat(event.palletMaxCapacity),
      };
      this.loadingSpace = loadingSpace;
    }
    if (isValid) {
      loadingSpace = {
        ...this.loadingSpace,
        name: event.name ? event.name : '',
        length: parseFloat(event.firstPart.length),
        width: parseFloat(event.firstPart.width),
        height: parseFloat(event.firstPart.height),
        limit: event.firstPart.limit === null ? null : parseFloat(event.firstPart.limit),
        trailerLoadingSide: event.firstPart.trailerLoadingSide as LoadingSide,
      };
      if (this.chosenType === 1 || this.loadingSpace.type === 1) {
        loadingSpace.semiTrailerLoadingSide = event.firstPart.trailerLoadingSide as LoadingSide;
      }
      this.loadingSpace = loadingSpace;
    }
  }

  public createOwnContainer(): void {
    this.isQuick = false;
    this.loadingSpace = emptyLoadingSpace;
    this.loadingSpace.type = this.typeForm.value.type;
    this.chosenLoadingSpace = emptyLoadingSpace;
    this.nameToSend = undefined;
    this.idLoadingSpace = undefined;
    this.isEdit = true;
    this.isListVisible = false;
    this.savedStops = undefined;
  }

  public openTab(tabName: string): void {
    this.closeTabs();

    switch (tabName) {
      case 'set':
        this.isTabSet = true;
        break;
      case 'axle-loads':
        this.isTabAxle = true;
        break;
      case 'gravity-center':
        this.isTabGravityCenter = true;
        break;
      case 'wheel-arches':
        this.isTabWheelArches = true;
        break;
      case 'cooler':
        this.isTabCooler = true;
        break;
      case 'door-size':
        this.isTabDoorSize = true;
        break;
      case 'variofloor':
        this.isTabVarioFloor = true;
        break;
      case 'palletStacking':
        this.isPalletStacking = true;
        break;
      case 'palletVehicleLoading':
        this.isPalletVehicleLoading = true;
        break;
      default:
    }
  }

  public closeTabs(): void {
    this.isTabSet = false;
    this.isTabAxle = false;
    this.isTabGravityCenter = false;
    this.isTabWheelArches = false;
    this.isTabCooler = false;
    this.isTabDoorSize = false;
    this.isTabVarioFloor = false;
    this.isPalletStacking = false;
    this.isPalletVehicleLoading = false;
  }

  public axlesSave(axles: Axle[]): void {
    this.loadingSpace = {
      ...this.addAxis(axles, this.loadingSpace),
    };
    this.chosenLoadingSpace = this.loadingSpace;
  }

  public editLoadingSpace(loadingSpace: LoadingSpaceType): void {
    if (this.isQuickMode && this.openedLoadingSpace === loadingSpace.name) {
      this.isQuick = !this.isQuick;
    } else if (this.isQuickMode && this.isQuick && this.openedLoadingSpace !== loadingSpace.name) {
      this.isQuick = false;
    }
    this.isEdit = true;
    this.loadingSpace = loadingSpace;
    this.chosenLoadingSpace = loadingSpace;
    this.openedLoadingSpace = loadingSpace.name;
    this.savedStops = undefined;
    this.isListVisible = false;
  }

  public enableEdit(): void {
    if (this.loadingSpace && !this.isEdit) {
      this.isEdit = true;
    }
  }

  private addAxis(axles: Axle[], loadingSpace: LoadingSpaceType): LoadingSpaceType {
    const modifiedLoadingSpace: LoadingSpaceType = {
      ...loadingSpace,
      axis: axles,
    };
    return modifiedLoadingSpace;
  }

  public onChosen(loadingSpace: LoadingSpaceType): void {
    this.isEdit = false;
    this.loadingSpace = loadingSpace;
    this.chosenLoadingSpace = loadingSpace;
    this.nameToSend = loadingSpace.name;
    this.idLoadingSpace = loadingSpace.id;
    this.savedStops = undefined;
  }

  public scrollDisable(disable: boolean): void {
    if (disable) {
      this.renderer.addClass(this.elRef.nativeElement, 'noScroll');
    } else {
      this.renderer.removeClass(this.elRef.nativeElement, 'noScroll');
    }
  }

  public isSet(): boolean {
    return this.dimensionsComponent.form.get('set').value as boolean;
  }

  public addSpace(event: LoadingSpaceType): void {
    this.loadingSpace = { ...event };
    this.submit(false);
  }

  public submitEvent(event: FormEvent, formName: string): void {
    switch (formName) {
      case 'GravityCenter': {
        const loadingSpace = {
          ...this.loadingSpace,
          gravityTrailerLengthTolerance: event.gravityTrailerLengthTolerance
            ? event.gravityTrailerLengthTolerance
            : null,
          gravitySemiTrailerLengthTolerance: event.gravitySemiTrailerLengthTolerance
            ? event.gravitySemiTrailerLengthTolerance
            : null,
          gravityShowCenter: event.gravityShowCenter,
          gravityTrailerWidthTolerance: event.gravityTrailerWidthTolerance
            ? event.gravityTrailerWidthTolerance
            : null,
          trailerContainerWeight: event.trailerContainerWeight
            ? event.trailerContainerWeight
            : null,
          gravitySemiTrailerWidthTolerance: event.gravitySemiTrailerWidthTolerance
            ? event.gravitySemiTrailerWidthTolerance
            : null,
          semiTrailerContainerWeight: event.semiTrailerContainerWeight
            ? event.semiTrailerContainerWeight
            : null,
        };
        this.loadingSpace = loadingSpace;
        break;
      }
      case 'Door': {
        const loadingSpace = {
          ...this.loadingSpace,
          trailerDoorHeight: event.trailerDoorHeight ? parseFloat(event.trailerDoorHeight) : null,
          trailerDoorWidth: event.trailerDoorWidth ? parseFloat(event.trailerDoorWidth) : null,
          semiTrailerDoorHeight: event.semiTrailerDoorHeight
            ? parseFloat(event.semiTrailerDoorHeight)
            : null,
          semiTrailerDoorWidth: event.semiTrailerDoorWidth
            ? parseFloat(event.semiTrailerDoorWidth)
            : null,
        };
        this.loadingSpace = loadingSpace;
        break;
      }
      case 'PalletVehicle': {
        const loadingSpace = {
          ...this.loadingSpace,
          palletVehicleToLoad: event.palletVehicleToLoad,
          palletVehicle: event.palletVehicle,
        };
        this.loadingSpace = loadingSpace;
        break;
      }
      case 'PalletStacking': {
        const loadingSpace = {
          ...this.loadingSpace,
          palletStacking: event.palletStacking,
          palletFloorNumber: event.palletFloorNumber,
          palletFloorWeight: event.palletFloorWeight,
          palletMaxWeight: event.palletMaxWeight,
        };
        this.loadingSpace = loadingSpace;
        break;
      }
      case 'VarioFloor': {
        const loadingSpace = {
          ...this.loadingSpace,
          varioTrailerFloorHeight: event.floorHeight ? parseFloat(event.floorHeight) : null,
          varioSemiTrailerFloorHeight: event.floorHeight2 ? parseFloat(event.floorHeight2) : null,
          varioTrailerFloorThickness: event.floorThickness
            ? parseFloat(event.floorThickness)
            : null,
          varioSemiTrailerFloorThickness: event.floorThickness2
            ? parseFloat(event.floorThickness2)
            : null,
          varioTrailerFloorCapacity: event.floorMaximumCapacity
            ? parseFloat(event.floorMaximumCapacity) >= 0
              ? parseFloat(event.floorMaximumCapacity)
              : null
            : null,
          varioSemiTrailerFloorCapacity: event.floorMaximumCapacity2
            ? parseFloat(event.floorMaximumCapacity2) >= 0
              ? parseFloat(event.floorMaximumCapacity2)
              : null
            : null,
        };
        this.loadingSpace = loadingSpace;
        break;
      }
      case 'Cooler': {
        const loadingSpace = {
          ...this.loadingSpace,
          coolerTrailerWidth: event.width ? parseFloat(event.width) : null,
          coolerTrailerHeight: event.height ? parseFloat(event.height) : null,
          coolerTrailerLength: event.length ? parseFloat(event.length) : null,
          coolerTrailerFloorHeight: event.floorHeight ? parseFloat(event.floorHeight) : null,
          coolerSemiTrailerWidth: event.width2 ? parseFloat(event.width2) : null,
          coolerSemiTrailerHeight: event.height2 ? parseFloat(event.height2) : null,
          coolerSemiTrailerLength: event.length2 ? parseFloat(event.length2) : null,
          coolerSemiTrailerFloorHeight: event.floorHeight2 ? parseFloat(event.floorHeight2) : null,
        };
        this.loadingSpace = loadingSpace;
        break;
      }
      case 'Wheel': {
        const loadingSpace = {
          ...this.loadingSpace,
          wheelTrailerWidth: event.width ? parseFloat(event.width) : null,
          wheelTrailerHeight: event.height ? parseFloat(event.height) : null,
          wheelTrailerLength: event.length ? parseFloat(event.length) : null,
          wheelTrailerDistance: event.distance ? parseFloat(event.distance) : null,
          wheelSemiTrailerWidth: event.width2 ? parseFloat(event.width2) : null,
          wheelSemiTrailerHeight: event.height2 ? parseFloat(event.height2) : null,
          wheelSemiTrailerLength: event.length2 ? parseFloat(event.length2) : null,
          wheelSemiTrailerDistance: event.distance2 ? parseFloat(event.distance2) : null,
        };
        this.loadingSpace = loadingSpace;
        break;
      }
      case 'Set': {
        const loadingSpace = {
          ...this.loadingSpace,
          set: event.set,
          length1: event.length1 ? parseFloat(event.length1) : null,
          height1: event.height1 ? parseFloat(event.height1) : null,
          width1: event.width1 ? parseFloat(event.width1) : null,
          limit1: event.limit1 ? parseFloat(event.limit1) : null,
        };
        if (this.chosenType === 1 || this.loadingSpace.type === 1) {
          loadingSpace.semiTrailerLoadingSide = this.loadingSpace.trailerLoadingSide
            ? this.loadingSpace.trailerLoadingSide
            : 'back';
        } else {
          loadingSpace.semiTrailerLoadingSide = event.semiTrailerLoadingSide
            ? event.semiTrailerLoadingSide
            : null;
        }
        this.loadingSpace = loadingSpace;
        break;
      }
    }
  }

  private saveAirContainer(shouldSave: boolean): void {
    const loadingSpace: Partial<LoadingSpaceType> = {
      name: this.loadingSpace.name ? this.loadingSpace.name : 'truck_without_name',
      width: this.loadingSpace.airWidth,
      height: this.loadingSpace.airHeightC,
      length: this.loadingSpace.airLengthA,
      limit: this.loadingSpace.limit ? this.loadingSpace.limit : 0,
      trailerLoadingSide: 'top',
      semiTrailerLoadingSide: 'top',
      set: false,
      type: this.typeForm.value.type,
    };

    let loadingSpaceAdditionalFeatures: Partial<LoadingSpaceType> = {
      ...loadingSpace,
      airType: this.loadingSpace.airType,
      airLengthA: this.loadingSpace.airLengthA,
      airWidth: this.loadingSpace.airWidth,
      airBaseB: this.loadingSpace.airBaseB,
      airHeightC: this.loadingSpace.airHeightC,
      airHeightD: this.loadingSpace.airHeightD,
      gravitySemiTrailerLengthTolerance: this.loadingSpace.gravitySemiTrailerLengthTolerance,
      gravityShowCenter:
        this.loadingSpace.gravityShowCenter === null ? false : this.loadingSpace.gravityShowCenter,
      gravityTrailerLengthTolerance: this.loadingSpace.gravityTrailerLengthTolerance,
      gravityTrailerWidthTolerance: this.loadingSpace.gravityTrailerWidthTolerance,
      gravitySemiTrailerWidthTolerance: this.loadingSpace.gravitySemiTrailerWidthTolerance,
      trailerContainerWeight: this.loadingSpace.trailerContainerWeight,
      semiTrailerContainerWeight: this.loadingSpace.semiTrailerContainerWeight,
    };
    const isMm = this.project.options.unit === 'mm';
    if (isMm) {
      loadingSpaceAdditionalFeatures = convertLoadingSpaceDimensions(
        loadingSpaceAdditionalFeatures,
        false,
      );
    }
    const loadingSpaceAdd = {
      currentWorkSpaceId: 1,
      newLoadingSpaces: {
        quantity: parseFloat(
          (this.dimensionsComponent as LoadingSpaceAirDimensionsComponent).form.get('airQuantity')
            .value,
        ),
        stops: this.savedStops,
        loadingSpace: {
          ...loadingSpaceAdditionalFeatures,
          id: this.idLoadingSpace ? this.idLoadingSpace : null,
        },
      },
    };
    this.addAndSaveLoadingSpace(loadingSpaceAdd, loadingSpaceAdditionalFeatures, shouldSave);
  }

  private saveVehicle(shouldSave: boolean): void {
    const trailerLoadingSide = this.loadingSpace.trailerLoadingSide
      ? this.loadingSpace.trailerLoadingSide
      : 'back';
    const semiTrailerLoadingSide =
      this.loadingSpace.set && (this.typeForm.value.type === 1 || this.loadingSpace.type === 1)
        ? trailerLoadingSide
        : this.loadingSpace.set
          ? this.loadingSpace.semiTrailerLoadingSide
          : undefined;
    const loadingSpace: Partial<LoadingSpaceType> = {
      name: this.loadingSpace.name ? this.loadingSpace.name : 'truck_without_name',
      length: parseFloat(
        (this.dimensionsComponent as LoadingSpaceDimensionsComponent).form.value.firstPart.length,
      ),
      width: parseFloat(
        (this.dimensionsComponent as LoadingSpaceDimensionsComponent).form.value.firstPart.width,
      ),
      height: parseFloat(
        (this.dimensionsComponent as LoadingSpaceDimensionsComponent).form.value.firstPart.height,
      ),
      limit: (this.dimensionsComponent as LoadingSpaceDimensionsComponent).form.value.firstPart
        .limit
        ? parseFloat(
            (this.dimensionsComponent as LoadingSpaceDimensionsComponent).form.value.firstPart
              .limit,
          )
        : 0,
      set: this.loadingSpace.set,
      length1: this.loadingSpace.set ? this.loadingSpace.length1 : undefined,
      width1: this.loadingSpace.set ? this.loadingSpace.width1 : undefined,
      height1: this.loadingSpace.set ? this.loadingSpace.height1 : undefined,
      trailerLoadingSide,
      semiTrailerLoadingSide,
      axis: this.loadingSpace.axis?.length > 0 ? this.loadingSpace.axis : undefined,
      limit1: this.loadingSpace.set && this.loadingSpace.limit1 ? this.loadingSpace.limit1 : 0,
      type: this.typeForm.value.type,
    };
    let loadingSpaceAdditionalFeatures: Partial<LoadingSpaceType> = {
      ...loadingSpace,
      coolerTrailerWidth: this.loadingSpace.coolerTrailerWidth,
      coolerTrailerHeight: this.loadingSpace.coolerTrailerHeight,
      coolerTrailerLength: this.loadingSpace.coolerTrailerLength,
      coolerTrailerFloorHeight: this.loadingSpace.coolerTrailerFloorHeight,
      coolerSemiTrailerWidth: this.loadingSpace.coolerSemiTrailerWidth,
      coolerSemiTrailerHeight: this.loadingSpace.coolerSemiTrailerHeight,
      coolerSemiTrailerLength: this.loadingSpace.coolerSemiTrailerLength,
      coolerSemiTrailerFloorHeight: this.loadingSpace.coolerSemiTrailerFloorHeight,
      wheelTrailerWidth: this.loadingSpace.wheelTrailerWidth,
      wheelTrailerHeight: this.loadingSpace.wheelTrailerHeight,
      wheelTrailerLength: this.loadingSpace.wheelTrailerLength,
      wheelTrailerDistance: this.loadingSpace.wheelTrailerDistance,
      wheelSemiTrailerWidth: this.loadingSpace.wheelSemiTrailerWidth,
      wheelSemiTrailerHeight: this.loadingSpace.wheelSemiTrailerHeight,
      wheelSemiTrailerLength: this.loadingSpace.wheelSemiTrailerLength,
      wheelSemiTrailerDistance: this.loadingSpace.wheelSemiTrailerDistance,
      varioTrailerFloorHeight: this.loadingSpace.varioTrailerFloorHeight,
      varioSemiTrailerFloorHeight: this.loadingSpace.varioSemiTrailerFloorHeight,
      varioTrailerFloorThickness: this.loadingSpace.varioTrailerFloorThickness,
      varioSemiTrailerFloorThickness: this.loadingSpace.varioSemiTrailerFloorThickness,
      varioTrailerFloorCapacity: this.loadingSpace.varioTrailerFloorCapacity,
      varioSemiTrailerFloorCapacity: this.loadingSpace.varioSemiTrailerFloorCapacity,
      trailerDoorHeight: this.loadingSpace.trailerDoorHeight,
      semiTrailerDoorHeight: this.loadingSpace.semiTrailerDoorHeight,
      trailerDoorWidth: this.loadingSpace.trailerDoorWidth,
      semiTrailerDoorWidth: this.loadingSpace.semiTrailerDoorWidth,
      gravitySemiTrailerLengthTolerance: this.loadingSpace.gravitySemiTrailerLengthTolerance,
      gravityShowCenter: this.loadingSpace.gravityShowCenter
        ? this.loadingSpace.gravityShowCenter
        : false,
      gravityTrailerLengthTolerance: this.loadingSpace.gravityTrailerLengthTolerance,
      gravityTrailerWidthTolerance: this.loadingSpace.gravityTrailerWidthTolerance,
      gravitySemiTrailerWidthTolerance: this.loadingSpace.gravitySemiTrailerWidthTolerance,
      trailerContainerWeight: this.loadingSpace.trailerContainerWeight,
      semiTrailerContainerWeight: this.loadingSpace.semiTrailerContainerWeight,
      unit: this.project.options.unit === 'mm' ? 'cm' : this.project.options.unit,
    };

    const isMm = this.project.options.unit === 'mm';
    if (isMm) {
      loadingSpaceAdditionalFeatures = convertLoadingSpaceDimensions(
        loadingSpaceAdditionalFeatures,
        false,
      );
    }
    const loadingSpaceAdd = {
      currentWorkSpaceId: 1,
      newLoadingSpaces: {
        quantity: parseFloat(
          (this.dimensionsComponent as LoadingSpaceDimensionsComponent).form
            .get('firstPart')
            .get('quantity').value,
        ),
        stops: this.savedStops,
        loadingSpace: {
          ...loadingSpaceAdditionalFeatures,
          id: this.idLoadingSpace ? this.idLoadingSpace : null,
        },
      },
    };
    this.addAndSaveLoadingSpace(loadingSpaceAdd, loadingSpaceAdditionalFeatures, shouldSave);
  }

  public onPreviewClicked(event: string[]): void {
    const clickedElement = event[0];
    switch (clickedElement) {
      case 'SetBasis':
        this.openTab('set');
        break;
      case 'WheelMesh':
        this.openTab('wheel-arches');
        break;
      case 'WheelMeshSet':
        this.openTab('wheel-arches');
        break;
      case 'VarioFloor':
        this.openTab('variofloor');
        break;
      case 'VarioFloorSet':
        this.openTab('variofloor');
        break;
      case 'CoolerMesh':
        this.openTab('cooler');
        break;
      case 'CoolerMeshSet':
        this.openTab('cooler');
        break;
      case 'Axis':
        this.openTab('axle-loads');
        break;
      default:
    }
  }

  private savePallet(shouldSave: boolean): void {
    const loadingSpace: Partial<LoadingSpaceType> = {
      name: this.loadingSpace.name ? this.loadingSpace.name : 'truck_without_name',
      length: this.loadingSpace.length,
      height: this.loadingSpace.height,
      width: this.loadingSpace.width,
      limit: this.loadingSpace.limit ? this.loadingSpace.limit : 0,
      set: false,
      type: this.typeForm.value.type,
      trailerLoadingSide: 'top',
      semiTrailerLoadingSide: 'top',
      palletWeight: this.loadingSpace.palletWeight,
      palletMaxHeight: this.loadingSpace.palletMaxHeight,
      palletStacking: this.loadingSpace.palletStacking,
      palletFloorNumber: this.loadingSpace.palletFloorNumber,
      palletFloorWeight: this.loadingSpace.palletFloorWeight,
      palletVehicleToLoad: this.loadingSpace.palletVehicleToLoad
        ? this.loadingSpace.palletVehicleToLoad
        : null,
      palletVehicle: this.loadingSpace.palletVehicle ? this.loadingSpace.palletVehicle : null,
      palletMaxWeight: this.loadingSpace.palletMaxWeight ? this.loadingSpace.palletMaxWeight : null,
      palletHeight: this.loadingSpace.palletHeight ? this.loadingSpace.palletHeight : null,
    };
    const isMm = this.project.options.unit === 'mm';
    let loadingSpaceAdditionalFeatures: Partial<LoadingSpaceType> = loadingSpace;
    if (isMm) {
      loadingSpaceAdditionalFeatures = convertLoadingSpaceDimensions(loadingSpace, false);
    }
    const loadingSpaceAdd = {
      currentWorkSpaceId: 1,
      newLoadingSpaces: {
        quantity: (this.dimensionsComponent as LoadingSpacePalletDimensionsComponent).form.value
          .palletQuantity,
        stops: this.savedStops,
        loadingSpace: {
          ...loadingSpaceAdditionalFeatures,
          id: this.idLoadingSpace ? this.idLoadingSpace : null,
        },
      },
    };
    this.addAndSaveLoadingSpace(loadingSpaceAdd, loadingSpace, shouldSave);
  }

  private getProjectVersions(): void {
    this.projectApi.getProjectVersions().subscribe((versions: VersionInfo[]): void => {
      if (versions === null) return;
      const versionsLength: number = versions.length;
      const currentIndex: number = versions.findIndex((version: VersionInfo) => version.isCurrent);
      this.isSharedProjectLast = versionsLength - 1 === currentIndex;
    });
  }

  private addAndSaveLoadingSpace(
    loadingSpaceAdd,
    loadingSpace: Partial<LoadingSpaceType>,
    shouldSave: boolean,
  ): void {
    const localStorageData: { data: string; date: string } = getFromLocalStorage('addLoadingSpace');
    const isPassedSevenDays: boolean = localStorageData
      ? isMoreThanDaysAgo(new Date(localStorageData.date), 1)
      : false;
    if (isPassedSevenDays) {
      localStorage.removeItem('addLoadingSpace');
    }
    const isPopupTouched: boolean = localStorageData ? localStorageData.data === 'true' : false;
    if (!this.isSharedProjectLast && !isPopupTouched) {
      this.displaySharedChangesEditPopup().subscribe((result) => {
        if (result.data === 'yes') {
          this.projectApi.addLoadingSpace(loadingSpaceAdd).subscribe(() => {
            this.submitted.emit();
            if (shouldSave) {
              this.userApi.saveLoadingSpace(loadingSpace).subscribe();
            }
          });
        } else {
          return;
        }
        setLocalStorage('true', 'addLoadingSpace');
      });
    } else {
      this.addAndSaveLoadingSpaceBody(loadingSpaceAdd, loadingSpace, shouldSave);
    }
  }

  private addAndSaveLoadingSpaceBody(
    loadingSpaceAdd: LoadingSpace,
    loadingSpace: Partial<LoadingSpaceType>,
    shouldSave: boolean,
  ): void {
    this.projectApi.addLoadingSpace(loadingSpaceAdd).subscribe(() => {
      this.submitted.emit();
      if (shouldSave) {
        loadingSpace.unit = undefined;
        this.userApi.saveLoadingSpace(loadingSpace).subscribe();
      }
    });
  }

  public submit(shouldSave: boolean): void {
    if (this.chosenType <= 2) {
      this.saveVehicle(shouldSave);
    }
    if (this.chosenType === 3) {
      this.savePallet(shouldSave);
    }
    if (this.chosenType === 4) {
      this.saveAirContainer(shouldSave);
    }
  }

  private sortByVolume(loadingSpaces: LoadingSpaceType[]) {
    return loadingSpaces.sort(
      (a, b) =>
        a.length * a.width * a.height +
        a.length1 * a.width1 * a.height1 -
        (b.length * b.width * b.height + b.length1 * b.width1 * b.height1),
    );
  }

  public onSearch(value: string): void {
    this.searchValue = value;
    const filteredLoadingSpaces = this.filterLoadingSpaceByType(this.typeForm.value.type);
    if (value !== '') {
      this.filteredLoadingSpaces = filteredLoadingSpaces.filter((space) => {
        return space.name.toLowerCase().includes(value.toLowerCase());
      });
    }
  }

  private filterLoadingSpaceByType(type: number): LoadingSpaceType[] {
    let filteredLoadingSpaces = this.loadingSpaces
      .filter((space) => space.useInRecommendation === true)
      .filter((loadingSpace) => loadingSpace.type === type);
    if (this.isSortByNameEnabled) {
      filteredLoadingSpaces = filteredLoadingSpaces.map((loadingSpace: LoadingSpaceType) => {
        return {
          ...loadingSpace,
          translatedName: this.translateLoadingSpaceName(loadingSpace.name),
        };
      });
      filteredLoadingSpaces = this.userService.sortByTranslatedName(
        filteredLoadingSpaces,
      ) as LoadingSpaceType[];
    }
    this.isListVisible = true;
    return filteredLoadingSpaces;
  }

  private translateLoadingSpaceName(loadName: string) {
    if (loadName) {
      return this.translate.instant(loadName) as string;
    } else {
      return loadName;
    }
  }

  private generateTypesForm() {
    return new UntypedFormGroup({
      type: new UntypedFormControl(this.chosenType),
    });
  }
}

export interface TypeForm extends UntypedFormGroup {
  value: {
    type: number;
  };
}
